import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { eachDayOfInterval, format } from "date-fns";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RINKS, RINK_LABEL, RINK_BADGE } from "@/lib/rinks";

const SEASON_DAYS = eachDayOfInterval({
  start: new Date("2026-11-14T00:00:00"),
  end: new Date("2027-01-17T00:00:00"),
}).map((d) => format(d, "yyyy-MM-dd"));

const HOURS = [
  ["10:00", "10am"], ["11:00", "11am"], ["12:00", "12pm"], ["13:00", "1pm"],
  ["14:00", "2pm"], ["15:00", "3pm"], ["16:00", "4pm"], ["17:00", "5pm"],
  ["18:00", "6pm"], ["19:00", "7pm"],
];

const GRID_BORDERS = "[&_th]:border-l-2 [&_th]:border-slate-900 [&_td]:border-l-2 [&_td]:border-slate-900 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0";

function WalkupGrid({ rink, readOnly = false, canClose = true }) {
  const [rows, setRows] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/walkups", { params: { rink } });
      const map = {};
      data.forEach((d) => { map[d.date] = d; });
      setRows(map);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [rink]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const setTop = () => {
      const h = document.querySelector("header");
      if (h) document.documentElement.style.setProperty("--summary-top", `${h.offsetHeight}px`);
    };
    setTop();
    window.addEventListener("resize", setTop);
    return () => window.removeEventListener("resize", setTop);
  }, []);

  const setCell = (date, time, value) =>
    setRows((r) => ({ ...r, [date]: { ...(r[date] || {}), counts: { ...((r[date] || {}).counts || {}), [time]: value } } }));

  const saveRow = async (date, extra = {}) => {
    if (readOnly) return;
    const row = { ...(rows[date] || {}), ...extra };
    const counts = {};
    HOURS.forEach(([t]) => {
      const v = Number((row.counts || {})[t]);
      if (v > 0) counts[t] = v;
    });
    setSaved((s) => ({ ...s, [date]: "Saving…" }));
    try {
      await api.put(`/walkups/${rink}/${date}`, { closed: !!row.closed, counts });
      setSaved((s) => ({ ...s, [date]: "Saved" }));
      setTimeout(() => setSaved((s) => ({ ...s, [date]: "" })), 1500);
    } catch (e) {
      setSaved((s) => ({ ...s, [date]: "" }));
      toast.error(errMsg(e));
    }
  };

  const totals = { grand: 0 };
  HOURS.forEach(([t]) => { totals[t] = 0; });
  SEASON_DAYS.forEach((d) => {
    const counts = (rows[d] || {}).counts || {};
    HOURS.forEach(([t]) => { totals[t] += Number(counts[t]) || 0; });
  });
  totals.grand = HOURS.reduce((a, [t]) => a + totals[t], 0);

  const sticky = { top: "var(--summary-top, 66px)" };

  return (
    <div className="mt-10" data-testid={`walkup-grid-${rink}`}>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <span className={`badge-brutal ${RINK_BADGE[rink]}`}>{RINK_LABEL[rink]}</span>
        <h2 className="font-display text-xl uppercase text-slate-900">Walk-up sessions</h2>
        <span className="text-xs text-slate-500">Walk-ups per session, 14 Nov 26 – 17 Jan 27. Tick Closed to mark a day off.{readOnly && " Read-only view."}</span>
      </div>
      <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a]" data-testid={`walkup-table-${rink}`}>
        <table className={`w-full table-fixed border-collapse ${GRID_BORDERS}`}>
          <thead>
            {!loading && (
              <tr className="bg-amber-300" data-testid={`walkup-totals-${rink}`}>
                <td className="p-2 w-[110px] sticky z-20 bg-amber-300 align-bottom" style={sticky}>
                  <div className="font-display text-sm uppercase font-bold">Season Totals</div>
                </td>
                <td className="p-2 w-[64px] sticky z-20 bg-amber-300" style={sticky}></td>
                {HOURS.map(([t, label]) => (
                  <td key={t} className="p-2 align-bottom text-center sticky z-20 bg-amber-200" style={sticky} data-testid={`walkup-total-${rink}-${t.replace(":", "")}`}>
                    <div className="font-mono-tight uppercase text-[9px] tracking-widest text-slate-600">{label}</div>
                    <div className="font-mono-tight text-sm font-bold">{totals[t]}</div>
                  </td>
                ))}
                <td className="p-2 w-[80px] align-bottom text-center sticky z-20 bg-amber-200" style={sticky} data-testid={`walkup-grand-total-${rink}`}>
                  <div className="font-mono-tight uppercase text-[9px] tracking-widest text-slate-600">Total</div>
                  <div className="font-mono-tight text-sm font-bold">{totals.grand}</div>
                </td>
              </tr>
            )}
            <tr className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest border-t-2 border-slate-900">
              <th className="p-2.5 text-center font-bold w-[110px]">Date</th>
              <th className="p-2.5 text-center font-bold w-[64px]">Closed</th>
              {HOURS.map(([t, label]) => (
                <th key={t} className="p-2.5 text-center font-bold bg-sky-800">{label}</th>
              ))}
              <th className="p-2.5 text-center font-bold w-[80px] bg-sky-800">Day Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={13} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid={`walkup-loading-${rink}`}>Loading…</td></tr>
            )}
            {!loading && SEASON_DAYS.map((date) => {
              const row = rows[date] || {};
              const counts = row.counts || {};
              const dayTotal = HOURS.reduce((a, [t]) => a + (Number(counts[t]) || 0), 0);
              return (
                <tr key={date} className={`border-t-2 border-slate-900 transition-colors ${row.closed ? "bg-slate-200 text-slate-500" : "hover:bg-sky-50/60"}`} data-testid={`walkup-row-${rink}-${date}`}>
                  <td className="p-2.5 text-sm font-bold whitespace-nowrap text-center">{format(new Date(`${date}T00:00:00`), "EEE dd MMM yy")}</td>
                  <td className="p-1.5 text-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-slate-700"
                      disabled={readOnly || !canClose}
                      title={canClose && !readOnly ? "Day closed" : "Only admin can close or reopen a day"}
                      checked={!!row.closed}
                      onChange={(e) => {
                        setRows((r) => ({ ...r, [date]: { ...(r[date] || {}), closed: e.target.checked } }));
                        saveRow(date, { closed: e.target.checked });
                      }}
                      data-testid={`walkup-closed-${rink}-${date}`}
                    />
                  </td>
                  {HOURS.map(([t]) => (
                    <td key={t} className={`p-1.5 ${row.closed ? "bg-slate-300" : "bg-sky-100"}`}>
                      <input
                        type="number" min="0" step="1"
                        className={`input-brutal !p-1.5 !shadow-none !text-xs !w-full !min-w-0 !text-center mx-auto block ${row.closed ? "!bg-slate-100 !text-slate-500" : ""}`}
                        disabled={readOnly || !!row.closed}
                        value={counts[t] ?? ""}
                        placeholder="0"
                        onChange={(e) => setCell(date, t, e.target.value)}
                        onBlur={() => saveRow(date)}
                        data-testid={`walkup-input-${rink}-${date}-${t.replace(":", "")}`}
                      />
                    </td>
                  ))}
                  <td className={`p-2.5 font-mono-tight text-sm font-bold text-center ${row.closed ? "bg-slate-300" : "bg-sky-200"}`} data-testid={`walkup-day-total-${rink}-${date}`}>
                    {dayTotal}
                    <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-normal" data-testid={`walkup-saved-${rink}-${date}`}>{saved[date] || " "}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SessionsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const readOnly = user.role === "viewer";
  const [params] = useSearchParams();
  const rinkFilter = params.get("rink");
  const shownRinks = user.role === "staff" ? [user.rink] : RINKS.filter((r) => !rinkFilter || r === rinkFilter);
  const [slots, setSlots] = useState({ sligo: [], clonmel: [] });
  const [newTime, setNewTime] = useState({ sligo: "", clonmel: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/slots");
      setSlots(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (rink, times) => {
    try {
      const { data } = await api.put(`/slots/${rink}`, { times });
      setSlots((s) => ({ ...s, [rink]: data.times }));
      toast.success(`${RINK_LABEL[rink]} sessions updated`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addTime = (rink) => {
    const t = newTime[rink];
    if (!t) return;
    if (slots[rink].includes(t)) {
      toast.error("That session time already exists");
      return;
    }
    save(rink, [...slots[rink], t]);
    setNewTime((n) => ({ ...n, [rink]: "" }));
  };

  const removeTime = (rink, t) => {
    save(rink, slots[rink].filter((x) => x !== t));
  };

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6" data-testid="sessions-page">
      <div className="mb-6">
        <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Public sessions</div>
        <h1 className="font-display text-3xl md:text-4xl uppercase text-slate-900">
          {user.role === "staff" ? `${RINK_LABEL[user.rink]} Walk-ups` : "Sessions & Walk-ups"}
        </h1>
        <p className="text-sm text-slate-600 mt-2">
          {isAdmin
            ? "Session slots feed the group booking dropdown. The walk-up grid documents how many walk-ups skated each session, per day."
            : "Enter how many walk-ups skated each session, per day."}
        </p>
      </div>

      {isAdmin && (loading ? (
        <div className="card-brutal p-8 text-center font-mono-tight text-xs uppercase tracking-widest font-bold">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {shownRinks.map((rink) => (
            <div key={rink} className="card-brutal shadow-[6px_6px_0px_0px_#0f172a] p-6" data-testid={`slots-card-${rink}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`badge-brutal ${RINK_BADGE[rink]}`}>{RINK_LABEL[rink]}</span>
                <span className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">{slots[rink].length} slots</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5 min-h-[40px]">
                {slots[rink].length === 0 && (
                  <span className="font-mono-tight text-xs uppercase tracking-widest text-slate-400">No sessions configured</span>
                )}
                {slots[rink].map((t) => (
                  <span key={t} className="badge-brutal bg-white text-slate-900 flex items-center gap-1.5" data-testid={`slot-${rink}-${t.replace(":", "")}`}>
                    {t}
                    <button onClick={() => removeTime(rink, t)} className="hover:text-rose-600" title="Remove" data-testid={`slot-remove-${rink}-${t.replace(":", "")}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="time"
                  className="input-brutal flex-1"
                  value={newTime[rink]}
                  onChange={(e) => setNewTime((n) => ({ ...n, [rink]: e.target.value }))}
                  data-testid={`slot-input-${rink}`}
                />
                <button className="btn-primary !px-4" onClick={() => addTime(rink)} data-testid={`slot-add-${rink}`}>
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {shownRinks.map((rink) => <WalkupGrid key={rink} rink={rink} readOnly={readOnly} canClose={isAdmin} />)}
    </div>
  );
}
